import React, { useRef, useState, useEffect } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';

const pillarData = [
  {
    id: 1,
    title: "Healthcare",
    description: "We are a growing healthcare network delivering comprehensive medical services across multiple touchpoints. From clinics and diagnostics to ambulance, homecare, and specialized care, we provide connected healthcare designed around patient needs. Our expanding ecosystem reflects our commitment to accessible, reliable, and high-quality care.",
    image: "https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?auto=format&fit=crop&q=80",
    cta: "Explore Our Network",
    href: "https://healthcare.svcaregroup.com/"
  },
  {
    id: 2,
    title: "Medical Academy",
    description: "We prepare the next generation of healthcare professionals with structured, practical training. Our focus is on building confidence, competence, and real clinical experience.",
    image: "https://images.unsplash.com/photo-1524178232363-1fb2b075b655?auto=format&fit=crop&q=80",
    cta: "View Programs",
    href: "https://academy.svcaregroup.com/"
  },
  {
    id: 3,
    title: "Bee Curious Preschool",
    description: "A nurturing preschool designed for early childhood development. We combine play, learning, and creativity to build strong foundations. Helping children grow with confidence and curiosity.",
    image: "https://images.unsplash.com/photo-1503454537195-1dcabb73ffb9?auto=format&fit=crop&q=80",
    cta: "Discover More",
    href: "https://www.beecurious.com.my/"
  }
];

function HeroCard({ pillar, index }) {
  return (
    <motion.div 
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-100px" }}
      transition={{ duration: 0.8, delay: index * 0.2 }}
      className="relative group w-full bg-black border border-white/10 rounded-[2.5rem] overflow-hidden min-h-[450px] md:min-h-[500px] flex flex-col items-center justify-center p-8 md:p-12 text-center"
    >
      {/* Background Image with 10% Opacity */}
      <div className="absolute inset-0 z-0">
        <img 
          src={pillar.image} 
          alt={pillar.title}
          className="w-full h-full object-cover opacity-10 group-hover:opacity-20 transition-opacity duration-700"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-transparent to-black/80" />
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-2xl flex flex-col items-center">
        <motion.span 
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          className="text-[#25a7df] font-bold uppercase tracking-[0.4em] text-[10px] md:text-xs mb-4"
        >
          Pillar 0{pillar.id}
        </motion.span>
        
        <h3 className="text-white text-3xl md:text-5xl font-serif font-bold mb-6 tracking-tight">
          {pillar.title}
        </h3>
        
        <p className="text-white/70 text-sm md:text-lg leading-relaxed font-light mb-10 max-w-xl text-balance">
          {pillar.description}
        </p>
        
        <motion.a 
          href={pillar.href}
          target="_blank"
          rel="noopener noreferrer"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          className="inline-block px-8 py-4 bg-[#25a7df] text-white text-xs md:text-sm font-bold rounded-full hover:bg-white hover:text-black transition-all duration-300 shadow-xl shadow-[#25a7df]/20 uppercase tracking-widest"
        >
          {pillar.cta}
        </motion.a>
      </div>

      {/* Decorative Border Glow */}
      <div className="absolute inset-0 border border-white/5 rounded-[2.5rem] pointer-events-none group-hover:border-[#25a7df]/30 transition-colors duration-500" />
    </motion.div>
  );
}

export default function ServicesIntro() {
  const containerRef = useRef(null);

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end end"]
  });

  const headerOpacity = useTransform(scrollYProgress, [0, 0.1, 0.9, 1], [0, 1, 1, 0]);
  const headerY = useTransform(scrollYProgress, [0, 0.1], [20, 0]);

  return (
    <section ref={containerRef} className="relative py-24 md:py-32 bg-gradient-to-b from-[#041d36] via-[#020a14] to-black">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        
        <motion.div 
          style={{ opacity: headerOpacity, y: headerY }}
          className="text-center mb-20 md:mb-32"
        >
          <span className="text-[#25a7df] font-bold uppercase tracking-[0.6em] mb-4 block text-xs md:text-sm">What We Offer</span>
          <h2 className="text-white text-4xl md:text-6xl lg:text-7xl font-sans font-bold tracking-tighter">Our Core Pillars</h2>
        </motion.div>

        <div className="flex flex-col gap-8 md:gap-16">
          {pillarData.map((pillar, idx) => (
            <HeroCard key={pillar.id} pillar={pillar} index={idx} />
          ))}
        </div>
      </div>

      {/* Ambient Background Light */}
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-full max-w-4xl h-[600px] bg-[#25a7df]/5 rounded-full blur-[120px] pointer-events-none z-0" />
    </section>
  );
}